import { Component, OnInit, Input, EventEmitter, Output, ElementRef, ViewChild, AfterViewInit, OnChanges, signal, computed } from '@angular/core';
import { FlowObjectDefinition, FlowObjectInstance, FlowObjectInstanceState, FlowObjectType } from '../../../models/flow-object.model';
import { ToastrService } from 'ngx-toastr';
import { Enums } from '../../../shared/enums';
import { AuthService } from '../../../services/auth.service';
import { EDocsService } from '../../../services/edocs.service';
import { Papel } from '../../../models/edocs.model';
import { Utils } from '../../../shared/utils';
import { FlowDefinition, FlowInstance, FlowInstanceState } from '../../../models/flow.model';
import { InputDataTaskGatewayPaths, OutputDataTaskGatewayPaths } from '../../../models/input-output-data.model';
import { IMPORTS } from './_imports';
import { ConfigSchema } from '../../../models/config-schema.model';
import { OrganogramaService } from '../../../services/organograma.service';

@Component({
    selector: 'fob-gateway-paths',
    templateUrl: './fob-gateway-paths.component.html',
    styleUrl: './fob-gateway-paths.component.scss',
    standalone: true,
    imports: IMPORTS
})
export class FlowObjectGatewayPathsComponent implements OnInit, OnChanges, AfterViewInit {
    // #region [ViewChild]
    @ViewChild('papelRef') papelRef: ElementRef;
    // #endregion

    // #region [Type props]
    FlowObjectType = FlowObjectType;
    Enums = Enums;
    // #endregion

    // #region [props]
    // #endregion

    // #region [signals]
    $model = signal(null as FlowObjectInstance);
    $flowInstance = signal(null as FlowInstance);
    $inputData = signal(null as InputDataTaskGatewayPaths);
    $outputData = signal(null as OutputDataTaskGatewayPaths);
    $flowObjectDefinition = signal(null as FlowObjectDefinition);
    $configSchema = signal(null as ConfigSchema);
    $papeisSelector = signal([] as Papel[]);
    $currentLocation = signal('');
    // #endregion

    // #region [computeds]
    $currentUser = computed(() => this.authService.$user());
    $hasPendingTask = computed(() => this.$model()?.stateId == FlowObjectInstanceState.Started);
    $isReadOnly = computed(() => [FlowObjectInstanceState.Finished, FlowObjectInstanceState.NotStarted].includes(this.$model()?.stateId) || this.$flowInstance()?.stateId === FlowInstanceState.CancelledByDeliberation);
    $chosenGatewayPathName = computed(() =>
        !this.$hasPendingTask()
        && this.$outputData().shouldFollowFirstGatewayPath
            ? this.$configSchema().taskGatewayPaths.firstGatewayPathName
            : this.$configSchema().taskGatewayPaths.secondGatewayPathName
    );
    $shouldLetPublicAgentInteract = computed(() =>
        this.$currentUser()?.isPublicAgent
        && this.$model()?.flowObjectInstanceActors.some(x => x.actorId == this.$currentUser()?.id)
        && (
            this.$flowInstance()?.ownerId != this.$currentUser()?.id
            || this.$currentUser()?.papeis?.some(x =>
                x.isCurrentUserHeadOfThisRolesOrganization
                && x.lotacaoGuid.trim().toLowerCase() == this.$flowInstance()?.currentActionableLocationId.trim().toLowerCase()
            )
        )
    );
    // #endregion

    // #region [Input/Output]
    @Input() _model: FlowObjectInstance;
    @Input() _flowInstance: FlowInstance;
    @Input() _flowDefinition: FlowDefinition;
    @Output() _submitEvent = new EventEmitter<FlowObjectInstance>();
    // #endregion

    constructor(
        private toastr: ToastrService,
        private authService: AuthService,
        private eDocsService: EDocsService,
        private organogramaService: OrganogramaService
    ) { }

    async ngOnInit() {
        Utils.setSignal(this.$flowObjectDefinition, this._flowDefinition.flowObjectDefinitions.find(x => x.id == this.$model().flowObjectDefinitionId));
        Utils.setSignal(this.$configSchema, JSON.parse(this.$flowObjectDefinition().configSchema) as ConfigSchema);
        Utils.setSignal(this.$inputData, Object.assign(new InputDataTaskGatewayPaths(), JSON.parse(this.$model().inputData)));
        Utils.setSignal(this.$outputData, this.$model().outputData == null ? null : Object.assign(new OutputDataTaskGatewayPaths(), JSON.parse(this.$model().outputData)));

        // #region [determinação do local atual]
        const response_LocationInfo = await this.organogramaService.getLocationInfo(this.$inputData().eDocsData.currentForwardingLocationId);

        if (!response_LocationInfo.isSuccess) {
            this.toastr.error(response_LocationInfo.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        let currentLocation = `${response_LocationInfo.data.nome} - ${response_LocationInfo.data.paiSigla}`;
        currentLocation += currentLocation.endsWith(response_LocationInfo.data.patriarcaSigla) ? '' : ` - ${response_LocationInfo.data.patriarcaSigla}`;
        this.$currentLocation.set(currentLocation);
        // #endregion

        // #region [seletor de papéis]
        const response_UsuarioPapeisEncaminhamento = await this.eDocsService.getUsuarioPapeisEncaminhamento(this.$inputData().eDocsData.forwardingId);

        if (!response_UsuarioPapeisEncaminhamento.isSuccess) {
            this.toastr.error(response_UsuarioPapeisEncaminhamento.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        this.$papeisSelector.set(response_UsuarioPapeisEncaminhamento.data);

        // força o usuário a selecionar um papel manualmente
        setTimeout(() => Utils.updSignal(this.$inputData, x => {
            x.eDocsData.signerId = null;
            return x;
        }), 50);

        if (this.$papeisSelector().length == 0) {
            Utils.updSignal(this.$inputData, x => {
                x.eDocsData.signerId = this.$currentUser().id;
                return x;
            });
        }
        // #endregion
    }

    ngOnChanges() {
        this.$model.set(this._model);
        this.$flowInstance.set(this._flowInstance);
    }

    ngAfterViewInit() {
        if (this.$hasPendingTask() && this.$inputData().eDocsData.signerId == null) {
            Utils.updSignal(this.$inputData, x => {
                x.eDocsData.signerId = this.$currentUser().papeis.find(x => x.prioritario).guid;
                return x;
            });
        }
    }

    signerIdChange(event) {
        Utils.updSignal(this.$inputData, x => {
            x.eDocsData.signerId = event;
            return x;
        });
    }

    submit(hasChosenFirstGatewayPath: boolean) {
        if (!this.validateInput()) return;

        Utils.updSignal(this.$inputData, x => {
            x.hasChosenFirstGatewayPath = hasChosenFirstGatewayPath;
            return x;
        });

        Utils.updSignal(this.$model, x => {
            x.inputData = JSON.stringify(this.$inputData());
            return x;
        });

        this._submitEvent.emit(this.$model());
    }

    // #region [===== private methods =====]
    // #endregion

    private validateInput(): boolean {
        if (Utils.isNullOrEmpty(this.$inputData().eDocsData.signerId)) {
            this.toastr.warning(Enums.Messages.NoSignerIdError, Enums.Messages.Warning, Utils.getToastrErrorOptions());
            return false;
        }

        return true;
    }
}
